import { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { LEDPanel } from '@/types/synoptique'

interface CustomPanelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreatePanel: (panel: LEDPanel) => void
}

interface CustomPanelForm {
  name: string
  resolutionWidth: string
  resolutionHeight: string
  dimensionsWidth: string
  dimensionsHeight: string
  brightness: string
  powerConsumption: string
}

const initialForm: CustomPanelForm = {
  name: '',
  resolutionWidth: '',
  resolutionHeight: '',
  dimensionsWidth: '',
  dimensionsHeight: '',
  brightness: '',
  powerConsumption: ''
}

export function CustomPanelDialog({
  open,
  onOpenChange,
  onCreatePanel
}: CustomPanelDialogProps) {
  const [form, setForm] = useState<CustomPanelForm>(initialForm)

  const handleCreate = () => {
    const resWidth = parseInt(form.resolutionWidth) || 0
    const resHeight = parseInt(form.resolutionHeight) || 0
    const dimWidth = parseInt(form.dimensionsWidth) || 0
    const dimHeight = parseInt(form.dimensionsHeight) || 0

    if (resWidth <= 0 || resHeight <= 0) {
      return
    }

    // Calculate pixel pitch from dimensions and resolution
    const pixelPitch = dimWidth > 0 && resWidth > 0
      ? Math.round((dimWidth / resWidth) * 100) / 100
      : 2.5

    // Auto-calculate dimensions if not provided
    const finalDimWidth = dimWidth > 0 ? dimWidth : Math.round(resWidth * pixelPitch)
    const finalDimHeight = dimHeight > 0 ? dimHeight : Math.round(resHeight * pixelPitch)

    const newPanel: LEDPanel = {
      id: `custom-panel-${Date.now()}`,
      name: form.name || `Custom ${resWidth}×${resHeight}`,
      brand: 'Custom',
      pixelPitch,
      resolution: { width: resWidth, height: resHeight },
      dimensions: { width: finalDimWidth, height: finalDimHeight },
      brightness: parseInt(form.brightness) || 5000,
      powerConsumption: parseInt(form.powerConsumption) || 150,
      ipRating: 'IP65'
    }

    onCreatePanel(newPanel)
    setForm(initialForm)
    onOpenChange(false)
  }

  const handleClose = () => {
    setForm(initialForm)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Créer un panneau personnalisé</DialogTitle>
          <DialogDescription>
            Entrez les spécifications de votre panneau LED. Seules les dimensions en pixels sont obligatoires.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Nom */}
          <div className="space-y-2">
            <Label htmlFor="panelName" className="text-sm font-semibold">Nom du panneau</Label>
            <Input
              id="panelName"
              type="text"
              placeholder="ex: Custom P2.5"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              Si vide, le nom sera généré automatiquement
            </p>
          </div>

          {/* Résolution */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="resolutionWidth" className="text-sm font-semibold">Largeur (px) *</Label>
              <Input
                id="resolutionWidth"
                type="number"
                placeholder="ex: 128"
                value={form.resolutionWidth}
                onChange={(e) => setForm(prev => ({ ...prev, resolutionWidth: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resolutionHeight" className="text-sm font-semibold">Hauteur (px) *</Label>
              <Input
                id="resolutionHeight"
                type="number"
                placeholder="ex: 128"
                value={form.resolutionHeight}
                onChange={(e) => setForm(prev => ({ ...prev, resolutionHeight: e.target.value }))}
                required
              />
            </div>
          </div>

          {/* Dimensions physiques */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dimensionsWidth" className="text-sm font-semibold">Largeur (mm)</Label>
              <Input
                id="dimensionsWidth"
                type="number"
                placeholder="ex: 500 (optionnel)"
                value={form.dimensionsWidth}
                onChange={(e) => setForm(prev => ({ ...prev, dimensionsWidth: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dimensionsHeight" className="text-sm font-semibold">Hauteur (mm)</Label>
              <Input
                id="dimensionsHeight"
                type="number"
                placeholder="ex: 500 (optionnel)"
                value={form.dimensionsHeight}
                onChange={(e) => setForm(prev => ({ ...prev, dimensionsHeight: e.target.value }))}
              />
            </div>
          </div>

          {/* Luminosité */}
          <div className="space-y-2">
            <Label htmlFor="brightness" className="text-sm font-semibold">Luminosité (cd/m²)</Label>
            <Input
              id="brightness"
              type="number"
              placeholder="ex: 5000 (optionnel)"
              value={form.brightness}
              onChange={(e) => setForm(prev => ({ ...prev, brightness: e.target.value }))}
            />
          </div>

          {/* Consommation */}
          <div className="space-y-2">
            <Label htmlFor="powerConsumption" className="text-sm font-semibold">Consommation (W)</Label>
            <Input
              id="powerConsumption"
              type="number"
              placeholder="ex: 150 (optionnel)"
              value={form.powerConsumption}
              onChange={(e) => setForm(prev => ({ ...prev, powerConsumption: e.target.value }))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button onClick={handleCreate}>
            Créer le panneau
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
